import { Injectable, OnDestroy } from '@angular/core';
import { logError, logFeature, logImpression, logUserAction } from '@office-start/telemetry-service';
import { Area, KnownErrorInstrumentationEvents, KnownFeatureNames, KnownImpressionInstrumentationEvents } from '@office-start/telemetry-types';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { AccessTokenConsumer } from '../../shared/services/access-token.service';
import { PerformanceMeasureType, PerformanceService } from '../../shared/services/performance.service';

export interface UiHostPerformanceInfo {
    clickTimeMarker: PerformanceMeasureType | string;
    instrumentationParams: InstrumentationParams;
}

export const enum FluidContentPerformanceMarker {
    DocumentLoadStart = 'FluidContent_Document_Load_Start',
    DocumentLoadPage = 'FluidContent_Document_Load_Page',
    DocumentLoadComponent = 'FluidContent_Document_Load_Component',
    DocumentLoadEnd = 'FluidContent_Document_Load_End'
}

@Injectable({
    providedIn: 'root'
})
export class FluidInstrumentationService implements OnDestroy {
    private fluidCorrelationId: string;
    private readonly performanceMarker: Map<number, UiHostPerformanceInfo>;

    constructor(
        private readonly instrumentationService: InstrumentationService,
        private readonly performanceService: PerformanceService
    ) {
        this.performanceMarker = new Map<number, UiHostPerformanceInfo>();
    }

    public ngOnDestroy(): void {
        this.fluidCorrelationId = undefined;
    }

    public setCorrelationId(correlationId: string) {
        this.fluidCorrelationId = correlationId;
    }

    public instrumentStart(eventName: KnownInstrumentationEvents): void {
        const params = {};
        params[InstrumentationParam.State] = 'Start';
        this.instrumentationService
            .logGenericEvent(eventName, this.getInstrumentationParams(params));
        logFeature({ featureName: KnownFeatureNames.Fluid, eventName, params });
    }

    public instrumentError(errorName: KnownInstrumentationEvents, otelErrorName: KnownErrorInstrumentationEvents, error: string): void {
        const errorMessage = error ? JSON.stringify(error) : '';
        const params: InstrumentationParams = this.getInstrumentationParams({
            [InstrumentationParam.Error]: errorMessage
        });
        this.instrumentationService
            .logErrorEvent(errorName, params);
        logError({errorType: 'Fluid_Error', eventName: otelErrorName, message: errorMessage, debugInfo: `FluidResource: ${params[InstrumentationParam.ResourceId]}`});
    }

    /**
     * Log BootError based on scenarioLifecycle
     */
    public instrumentBootError(scenarioLifecycle: string, error: string): void {
        if (scenarioLifecycle === 'Load') {
            this.instrumentFailure(KnownInstrumentationEvents.FluidContentOpen, error);
        } else {
            this.instrumentFailure(KnownInstrumentationEvents.FluidContentCreateNew, error);
        }
    }

    /**
     * Log success scenario
     */
    public instrumentSuccess(eventName: KnownInstrumentationEvents, versionHistorySession: boolean = false, debugInfo?: string, tokenConsumer?: AccessTokenConsumer): void {
        const params = {};
        params[InstrumentationParam.State] = 'Success';
        params[InstrumentationParam.VersionHistory] = versionHistorySession.toString();
        if (debugInfo) {
            params[InstrumentationParam.DebugInfo] = debugInfo;
        }
        if (tokenConsumer) {
            params[InstrumentationParam.Consumer] = tokenConsumer;
        }
        this.instrumentationService
            .logGenericEvent(eventName, this.getInstrumentationParams(params));
        logFeature({ featureName: KnownFeatureNames.Fluid, eventName, params });
    }

    /**
     * Log failure scenario
     */
    public instrumentFailure(eventName: KnownInstrumentationEvents, reason: any, versionHistorySession: boolean = false, tokenConsumer?: AccessTokenConsumer): void {
        const params = {};
        params[InstrumentationParam.State] = 'Fail';
        params[InstrumentationParam.Error] = reason ? JSON.stringify(reason) : '';
        params[InstrumentationParam.VersionHistory] = versionHistorySession ? 'true' : 'false';

        if (tokenConsumer) {
            params[InstrumentationParam.Consumer] = tokenConsumer;
        }
        this.instrumentationService
            .logGenericEvent(eventName, this.getInstrumentationParams(params));
        logFeature({ featureName: KnownFeatureNames.Fluid, eventName, params });
    }

    public logPerformanceEventFromClick(bootId: number, name: string) {
        const { clickTimeMarker, instrumentationParams }: UiHostPerformanceInfo = this.performanceMarker.get(bootId) ?? {
            clickTimeMarker: PerformanceMeasureType.FromBrowserNavigationStart,
            instrumentationParams: {
                [InstrumentationParam.ResourceId]: this.fluidCorrelationId
            }
        };

        this.performanceService.mark(`${name}.${bootId}`, clickTimeMarker, undefined, instrumentationParams);
    }

    public logPerformanceBootStart(
        bootId: number
    ) {
        const instrumentationParams: InstrumentationParams = {
            [InstrumentationParam.ResourceId]: this.fluidCorrelationId
        };

        const clickTimeMarker = this.performanceService.mark(
            `FluidContentPerformanceMarker.DocumentLoadStart.${bootId}`,
            PerformanceMeasureType.NoMeasure,
            undefined,
            instrumentationParams
        );

        this.performanceMarker.set(bootId, {
            clickTimeMarker,
            instrumentationParams
        });
    }

    public instrumentClickEvent(eventName: KnownInstrumentationEvents, elementId: string, area: string) {
        this.instrumentationService.logClickEvent(eventName, elementId, area);
        logUserAction({
            id: elementId, eventName, area: area as Area,
            result: 'Navigate',
            target: 'Other'
        });
    }

    public instrumentImpressionEvent(eventName: KnownInstrumentationEvents, otelEventName: KnownImpressionInstrumentationEvents, area: Area, params?: InstrumentationParams) {
        this.instrumentationService.logImpressionEvent(eventName, this.getInstrumentationParams(params));
        logImpression({ eventName: otelEventName, id: this.fluidCorrelationId, area });
    }

    private getInstrumentationParams(additionalParams: InstrumentationParams = {}): InstrumentationParams {
        const params: InstrumentationParams = {
            [InstrumentationParam.ResourceId]: this.fluidCorrelationId,
            ...additionalParams
        };

        return params;
    }
}
